import { Switch, Route } from "wouter";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SettingsProvider } from "./contexts/SettingsContext";
import { ProtectedRoute } from "./lib/ProtectedRoute";
import AuthPage from "./pages/auth-page";
import DashboardPage from "./pages/dashboard-page";
import LibraryPage from "./pages/library-page";
import EditDocumentPage from "./pages/edit-document-page";
import ViewDocumentPage from "./pages/view-document-page";
import CreateDocumentPage from "./pages/create-document-page";
import NotesPage from "./pages/notes-page";
import NotePage from "./pages/note/[id]";
import QuotesPage from "./pages/quotes-page";
import QuotePage from "./pages/quote/[id]";
import ScriptureReferencesPage from "./pages/scripture-references-page";
import ScriptureReferencePage from "./pages/scripture-reference/[id]";
import TagsPage from "./pages/tags-page";
import TagPage from "./pages/tag-page";
import SearchPage from "./pages/search-page";
import NotFound from "./pages/not-found";
import { SearchPalette } from "./components/search/SearchPalette";
import { ConnectionBanner } from "./components/layout/ConnectionBanner";
import { useAuth } from "./hooks/use-auth";
import { usePointerEventsGuard } from "./hooks/use-pointer-events-guard";
import { DesktopBridge } from "./components/desktop/DesktopBridge";
import { DropCapture } from "./components/desktop/DropCapture";
import { isPresenterSurface } from "./lib/runtime";
import PresentPage from "./pages/present-page";

function Router() {
  return (
    <Switch>
      <Route path="/auth" component={AuthPage} />

      <ProtectedRoute path="/documents/create" component={CreateDocumentPage} />
      <ProtectedRoute path="/documents/:id/edit" component={EditDocumentPage} />
      <ProtectedRoute path="/documents/:id" component={ViewDocumentPage} />

      <ProtectedRoute path="/notes" component={NotesPage} />
      <ProtectedRoute path="/notes/:id" component={NotePage} />

      <ProtectedRoute path="/quotes" component={QuotesPage} />
      <ProtectedRoute path="/quotes/:id" component={QuotePage} />

      <ProtectedRoute path="/scripture-references" component={ScriptureReferencesPage} />
      <ProtectedRoute path="/scripture-references/:id" component={ScriptureReferencePage} />

      <ProtectedRoute path="/tags" component={TagsPage} />
      <ProtectedRoute path="/tags/:id" component={TagPage} />

      <ProtectedRoute path="/search" component={SearchPage} />
      <ProtectedRoute path="/library" component={LibraryPage} />
      <ProtectedRoute path="/" component={DashboardPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function AuthenticatedChrome() {
  const { user } = useAuth();
  return (
    <>
      <ConnectionBanner />
      <Router />
      {user ? <SearchPalette /> : null}
    </>
  );
}

export default function App() {
  // One observer for the whole app — see the hook for what it is guarding.
  usePointerEventsGuard();

  if (isPresenterSurface()) {
    return <PresentPage />;
  }

  return (
    <AuthProvider>
      <ThemeProvider>
        <SettingsProvider>
          <div className="desktop-traffic-drag fixed left-0 top-0 z-[80] w-20" aria-hidden="true" />
          <DesktopBridge />
          <DropCapture />
          <AuthenticatedChrome />
        </SettingsProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
